(ns app.domain.verdict.common-render
  (:require #?(:clj  [app.time :as time]
               :cljs [core.front.date-util :as date-util])
            #?(:clj [core.util.edn :as edn])
            #?(:clj [hiccup.util])
            #?(:clj [potpuri.core :as p])
            [app.domain.business.model :as business-model]
            [app.domain.form.model :as form-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.property.model :as property-model]
            [app.domain.verdict.common-util :as cutil]
            [app.domain.workspace.model :as workspace-model]
            [app.rich-text :as rich-text]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.domain.system-of-units :as sou]))

#_{:clj-kondo/ignore #?(:cljs [:unused-binding] :clj [])}
(defn format-date
  "Format a date from an instant in the given time zone. On the frontend, always
  uses browser timezone."
  [tz date]
  #?(:clj  (p/condas-> date d
             (string? d) (edn/decode d)
             (inst? d) (time/format-local-date tz d))
     :cljs (date-util/date date)))

(defn value-entered?
  "True if a form-field value counts as entered. `false` is a real value
  (an unchecked checkbox), everything else is judged by its string form being non-blank.
  Shared by the permit-preview and PDF table renderers."
  [value]
  (or (false? value)
      (not (str/blank? (str value)))))

(defn cell-value-entered?
  "Whether a table cell holds an entered value, judged from its RAW value (not its
  rendered output, which can be non-blank even when empty — e.g. rich text renders
  an editor / <p></p>). Rich-text cells use `rich-text/empty-html-rich-text?`; other
  cells use `value-entered?`. Used to drop wholly-empty table rows while keeping every
  cell of a row that has any value."
  [{:keys [rich-text? value]}]
  (if rich-text?
    (not (rich-text/empty-html-rich-text? value))
    (value-entered? value)))

(defmulti
  ^{:arglists '([ctx section])}
  render
  "Accepts a context and verdict section. Returns a DOM hierarchy in hiccup format."
  (fn [_ section] (:verdict.section/type section)))

(defmethod render :default [_ section]
  #?(:clj  (throw (Exception. (str "No common renderer for section type: " (:verdict.section/type section))))
     :cljs (throw (js/Error (str "No common renderer for section type: " (:verdict.section/type section))))))

(defn- render-children [ctx section]
  [:div
   (for [child (->> (:entity/_parent section)
                    (sort-by :ordered/order))]
     (render ctx child))])

(defmethod render :verdict.section.type/template-page [ctx section]
  [:div.verdict-page.mt-6.mb-6
   (render-children ctx section)])

(defmethod render :verdict.section.type/section [ctx section]
  [:div.verdict-page__section
   [:h4 (cutil/content section)]
   (render-children (assoc ctx :section-title (cutil/content section)) section)])

(defn workspace-or-property-address [workspace property]
  (if (= (:workspace/primary-property workspace) property)
    (:workspace/address workspace)
    (-> property property-model/full-address)))

(defmethod render :verdict.section.type/permit-location [{:keys [workspace tr]} _]
  (let [all-properties    (->> workspace
                               :workspace/properties
                               (group-by :property/type))
        sorted-properties (->> all-properties
                               :property.type/property
                               (sort-by (workspace-model/property-sort-fn workspace)))
        sorted-roads      (->> all-properties
                               :property.type/road
                               (sort-by (workspace-model/property-sort-fn workspace)))]
    [:div
     (when (->> all-properties vals (apply concat) rest seq)
       [:div.bx--row
        [:div.verdict-static-label.responsive-column-half
         [:div.bx--label
          (tr [:workspace/primary-address])]
         [:div.label-value (:workspace/address workspace)]]])
     (when (seq sorted-properties)
       [:table.properties
        [:thead
         [:tr
          [:th (tr [:workspace/address])]
          [:th (tr [:zoning])]
          [:th (tr [:legal])]]]
        (into [:tbody]
              (for [property sorted-properties]
                [:tr
                 [:td (workspace-or-property-address workspace property)]
                 [:td (:property/zoning property)]
                 [:td (:property/legal property)]]))])
     (when (seq sorted-roads)
       [:div
        [:h5.mt-4 (tr [:property.type/roads])]
        [:table.properties
         [:thead
          [:tr
           [:th (tr [:workspace/address])]
           [:th (tr [:property/description])]]]
         (into [:tbody]
               (for [property sorted-roads]
                 [:tr
                  [:td (workspace-or-property-address workspace property)]
                  [:td (tr [:property/from-to]
                           [(:property/from-st property)
                            (:property/to-st property)])]]))]])]))

(defmethod render :verdict.section.type/property-attributes [{:keys [workspace tr]} _]
  (let [properties            (->> workspace :workspace/properties)
        properties-with-attrs (->> properties
                                   (filter #(-> % :property/legacy-attributes seq))
                                   (sort-by (workspace-model/property-sort-fn workspace)))]
    [:div
     (when (seq properties-with-attrs)
       ; If all properties have the same attribute values, no need to repeat them for every property
       (if (->> properties (map :property/legacy-attributes) (apply =))
         [:div.bx--row
          [:div.verdict-static-label.responsive-column-half
           [:div.bx--label
            (tr [:property/legacy-attributes])]
           [:div.label-value (->> properties first property-model/property-attrs-str)]]]
         ; Show table of attributes per property address
         [:table.properties
          [:thead
           [:tr
            [:th (tr [:workspace/address])]
            [:th (tr [:property/legacy-attributes])]]]
          (into [:tbody]
                (for [property properties-with-attrs]
                  [:tr
                   [:td (workspace-or-property-address workspace property)]
                   [:td (property-model/property-attrs-str property)]]))]))]))

(defmethod render :verdict.section.type/permit-base-data
  [{:keys [verdict application workspace tr verdict-target-state]} _]
  (let [tz (-> workspace :domain/authority-group :group/tz)]
    [:div.bx--row.permit-base-data
     [:div.verdict-static-label.responsive-column-half.municipal-permit-number
      [:div.bx--label
       (tr [:verdict/municipal-permit-number])]
      [:div.label-value
       (or (:verdict/municipal-permit-number verdict)
           (:workspace/id workspace))]]
     [:div.verdict-static-label.responsive-column-half.submitted-date
      [:div.bx--label
       (tr [:verdict/submitted-date])]
      [:div.label-value
       (or (when-let [dt (:application/submitted application)]
             (format-date tz dt))
           "-")]]
     [:div.verdict-static-label.responsive-column-half.issue-date
      [:div.bx--label
       (tr [:verdict/issue-date])]
      [:div.label-value
       (or (when-let [dt (:verdict/issue-date verdict)]
             (format-date tz dt))
           "-")]]
     [:div.verdict-static-label.responsive-column-half.status
      [:div.bx--label
       (tr [:verdict/status])]
      [:div.label-value
       (tr [(or verdict-target-state (:domain/state verdict))])]]]))


(defmethod render :verdict.section.type/roll-number [{:keys [tr workspace]} _]
  [:div.verdict-static-label.roll-number
   [:div.bx--label
    (tr [:property/id])]
   [:div.label-value
    (workspace-model/property-data workspace :property/id)]])


(defn form-field-value-wrapper [label value unit-label lang rich-text?]
  [:div.verdict-static-label
   [:div.bx--label (form-model/label-text label lang)]
   (when (or (and rich-text? (vector? value))
             (not (str/blank? value)))
     (if (some? unit-label)
       [:div.label-value value " " unit-label]
       [:div.label-value value]))])


(defmulti render-form-field-value :tag)


(defmethod render-form-field-value :default [{:keys [value unit]}]
  (cond
    (and unit (number? value)) (->> value
                                    (sou/round sou/default-precision)
                                    str)
    (string? value) (rich-text/html-to-text value)
    :else (str value)))


(defmethod render-form-field-value :date-picker [{:keys [value group-tz]}]
  (when value
    #?(:clj  (time/format-local-date (time/in-zone value group-tz))
       :cljs (date-util/date-in-tz value group-tz))))


(defmethod render-form-field-value :time-picker [{:keys [value group-tz]}]
  (when-some [[hour minute] value]
    #?(:clj  (time/format-local-time (time/today-at hour minute group-tz))
       :cljs (date-util/hour-minute-in-tz (date-util/today-at hour minute) group-tz))))


(defmethod render-form-field-value :checkbox [{:keys [value tr]}]
  (if value (tr [:1]) (tr [:0])))


(defmethod render-form-field-value :file-input [_]
  ;; No rendering for file input field values on permits.
  nil)


(defmethod render-form-field-value :signature [_]
  ;; No rendering for signature field values on permits.
  nil)


(defn get-role-restrictions [content]
  #?(:clj  (some-> content edn/decode)
     :cljs content))

#_{:clj-kondo/ignore #?(:cljs [:unused-binding] :clj [])}
(defmethod render :verdict.section.type/parties [{:keys [application tr lang] :as ctx} section]
  (let [role-restrictions (get-role-restrictions (:verdict.section/content section))
        localize-role     #(role-model/party-role-name % lang)
        parties           (->> application
                               party-model/active-parties
                               (filter #(or (nil? role-restrictions)
                                            (->> % party-model/role-keys (set/intersection role-restrictions) seq)))
                               (map (comp #(update % :party/party-roles (fn [roles] (sort (map localize-role roles))))
                                          eu/->map))
                               (sort-by #(-> % :party/party-roles first)))]
    (into
      [:div.bx--row]
      (for [party parties]
        [:div.party.verdict-static-label.responsive-column-half
         [:div.bx--label
          (->> (:party/party-roles party) ; these are already localized for sorting
               (str/join ", "))]
         [:div.label-value
          (when-some [org (party-model/get-user-key ctx party :user/corporation-or-partnership)]
            [:div org])
          (when (or (party-model/get-user-key ctx party :user/first-name)
                    (party-model/get-user-key ctx party :user/last-name))
            [:div (party-model/user-party-full-name party)])
          (when-some [street-address (party-model/get-user-key ctx party :user/street-address)]
            [:div street-address])
          (when-some [municipality (party-model/get-user-key ctx party :user/municipality)]
            [:div (str municipality
                       ", " (party-model/get-user-key ctx party :user/province)
                       " " (party-model/get-user-key ctx party :user/postal-code))])
          (when-let [business (:party/business party)]
            [:div
             [:div (business-model/business-name business lang)]
             (when-some [business-id (:business/id business)]
               [:div business-id])])
          #?(:clj (when-some [user-licences (some-> (:party/user party) :user/licences)]
                    (for [user-licence user-licences]
                      (when-some [licence-number (:user-licence/number user-licence)]
                        [:div (str (tr [(:user-licence/type user-licence)]) " " licence-number)]))))]]))))


(defmethod render :verdict.section.type/primary-address [{:keys [workspace]} _]
  [:div (:workspace/address workspace)])
